import React, { ChangeEvent, useCallback, useEffect } from 'react';
import { Container, FlexboxGrid, Heading } from 'rsuite';
import { FormikErrors, useFormik, FormikValues, FormikHelpers } from 'formik';
import CustomPaymentFormElements from './CustomPaymentFormElements';
import { US_STATES } from '../../../common/consts';
import { CustomPaymentData } from './types/customPayment';
import { FormValidations } from '../../form/constants';
import { emailPattern, zipCodePattern } from '../../../common/regex';

interface CustomFormProps<T extends FormikValues = {}> {
  additionalInitialValues?: T;
  additionalValidation?: (values: CustomPaymentData & T) => FormikErrors<T>;
  children?:
    | React.ReactNode
    | ((
        formik: FormikHelpers<FormikValues & T> & {
          values: FormikValues & T;
          errors: FormikErrors<FormikValues & T>;
        }
      ) => React.ReactNode);
  error?: string;
  heading?: string;
  loading?: boolean;
  onSubmit: (data: CustomPaymentData & T) => void;
  subHeading?: string;
  submitText?: string;
}

const us_states = Object.entries(US_STATES);

const regions = us_states.map((us_state) => ({
  label: us_state[1],
  value: us_state[0],
}));

export function CustomForm<T extends FormikValues = {}>({
  additionalInitialValues = {} as T,
  additionalValidation,
  children,
  error,
  heading,
  loading,
  onSubmit,
  subHeading,
  submitText,
}: CustomFormProps<T>) {
  const formik = useFormik<CustomPaymentData & T>({
    initialValues: {
      first_name: undefined,
      last_name: undefined,
      address: undefined,
      address2: undefined,
      city: undefined,
      region: undefined,
      postal_code: undefined,
      email: undefined,
      ...additionalInitialValues,
    } as CustomPaymentData & T,
    validate: (
      values: CustomPaymentData & T
    ): FormikErrors<CustomPaymentData & T> => {
      const errors: FormikErrors<CustomPaymentData> = {};

      if (!values.last_name) {
        errors.last_name = FormValidations.ERROR_REQUIRED;
      }
      if (!values.first_name) {
        errors.first_name = FormValidations.ERROR_REQUIRED;
      }
      if (!values.address) {
        errors.address = FormValidations.ERROR_REQUIRED;
      }
      if (!values.city) {
        errors.city = FormValidations.ERROR_REQUIRED;
      }
      if (!values.region) {
        errors.region = FormValidations.ERROR_REQUIRED;
      }
      if (!values.postal_code) {
        errors.postal_code = FormValidations.ERROR_REQUIRED;
      } else if (!zipCodePattern.test(values.postal_code)) {
        errors.postal_code = FormValidations.ERROR_ZIP_CODE;
      }
      if (!values.email) {
        errors.email = FormValidations.ERROR_REQUIRED;
      } else if (!emailPattern.test(values.email)) {
        errors.email = FormValidations.ERROR_EMAIL;
      }

      if (additionalValidation) {
        return { ...errors, ...additionalValidation(values) };
      }
      return errors;
    },
    onSubmit,
  });

  const [internalError, setInternalError] = React.useState<string>();

  useEffect(() => {
    if (error !== internalError) {
      setInternalError(error);
    }
  }, [error, internalError]);

  const updateForm = useCallback(
    (name: string, value: string | null) => {
      formik.setFieldValue(name, value);
      if (internalError) {
        setInternalError(undefined);
      }
    },
    [internalError, formik]
  );

  const updatePaymentInfo = useCallback(
    (value: string, e: ChangeEvent<HTMLInputElement>) =>
      updateForm(e.target.name, value),
    [updateForm]
  );

  const setRegion = useCallback(
    (value: string | null) => updateForm('region', value),
    [updateForm]
  );

  return (
    <Container
      style={{
        alignItems: 'center',
        padding: '1.5rem 1rem',
      }}
    >
      <CustomPaymentFormElements onSubmit={formik.handleSubmit}>
        <Container style={{ marginBottom: '1rem' }}>
          {!!heading && (
            <Heading
              level={4}
              style={{ color: '#5c5d5d' }}
            >
              {heading}
            </Heading>
          )}
          {!!subHeading && (
            <Heading
              level={5}
              style={{ color: '#5c5d5d' }}
            >
              {subHeading}
            </Heading>
          )}
        </Container>
        <CustomPaymentFormElements.Error error={error} />
        {typeof children == 'function' ? children(formik) : children}
        <CustomPaymentFormElements.Label>Name</CustomPaymentFormElements.Label>
        <div style={{ display: 'flex', gap: '0.5rem' }}>
          <CustomPaymentFormElements.FormGroup>
            <CustomPaymentFormElements.Input
              name="first_name"
              placeholder="First Name"
              onChange={updatePaymentInfo}
              value={formik.values.first_name ?? ''}
            />
            <CustomPaymentFormElements.Error error={formik.errors.first_name} />
          </CustomPaymentFormElements.FormGroup>
          <CustomPaymentFormElements.FormGroup>
            <CustomPaymentFormElements.Input
              name="last_name"
              placeholder="Last Name"
              onChange={updatePaymentInfo}
              value={formik.values.last_name ?? ''}
            />
            <CustomPaymentFormElements.Error error={formik.errors.last_name} />
          </CustomPaymentFormElements.FormGroup>
        </div>

        <CustomPaymentFormElements.FormGroup>
          <CustomPaymentFormElements.Label htmlFor="cardholderName">
            Email
          </CustomPaymentFormElements.Label>
          <CustomPaymentFormElements.Input
            name="email"
            required
            placeholder="Email"
            onChange={updatePaymentInfo}
            value={formik.values.email ?? ''}
          />
          <CustomPaymentFormElements.Error error={formik.errors.email} />
        </CustomPaymentFormElements.FormGroup>
        <CustomPaymentFormElements.FormGroup>
          <CustomPaymentFormElements.Label>
            Address
          </CustomPaymentFormElements.Label>
          <div>
            <CustomPaymentFormElements.Input
              required
              name="address"
              maxLength={30}
              onChange={updatePaymentInfo}
              placeholder="Address Line 1"
              value={formik.values.address ?? ''}
            />
            <CustomPaymentFormElements.Error error={formik.errors.address} />
            <CustomPaymentFormElements.Input
              id="address2"
              name="address2"
              maxLength={30}
              onChange={updatePaymentInfo}
              placeholder="Address Line 2 (optional)"
              value={formik.values.address2 ?? ''}
            />
            <CustomPaymentFormElements.Error error={formik.errors.address2} />
          </div>
          <FlexboxGrid justify="space-between">
            <FlexboxGrid.Item colspan={9}>
              <CustomPaymentFormElements.Input
                required
                name="city"
                maxLength={30}
                onChange={updatePaymentInfo}
                placeholder="City"
                value={formik.values.city ?? ''}
              />
              <CustomPaymentFormElements.Error error={formik.errors.city} />
            </FlexboxGrid.Item>
            <FlexboxGrid.Item colspan={9}>
              <CustomPaymentFormElements.Select
                data={regions}
                searchable
                cleanable
                name="region"
                onChange={setRegion}
                placeholder="State"
                value={formik.values.region ?? null}
                data-testid="region-select"
              />
              <CustomPaymentFormElements.Error error={formik.errors.region} />
            </FlexboxGrid.Item>
            <FlexboxGrid.Item colspan={5}>
              <CustomPaymentFormElements.Input
                required
                name="postal_code"
                maxLength={10}
                onChange={updatePaymentInfo}
                placeholder="Zip Code"
                value={formik.values.postal_code ?? ''}
                type="number"
              />
              <CustomPaymentFormElements.Error
                error={formik.errors.postal_code}
              />
            </FlexboxGrid.Item>
          </FlexboxGrid>
        </CustomPaymentFormElements.FormGroup>
        <CustomPaymentFormElements.PaymentButton loading={!!loading}>
          {submitText || 'Pay'}
        </CustomPaymentFormElements.PaymentButton>
      </CustomPaymentFormElements>
    </Container>
  );
}
